import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Typography } from '@mui/material';
import { memo, useEffect, useRef } from 'react';
import { useThrottledValue } from '../hooks/useThrottledValue';
import { useTypewriterMessage } from '../hooks/useTypewriterMessage';
import type { ApprovalRequest, ChatError, Message, StreamingStatus } from '../models';
import { isRetryableError } from '../services';
import {
  colorAgentBorder,
  colorAgentTextPrimary,
  colorAgentTextSecondary,
  colorNeutralGray,
  colorSemanticError,
  colorSemanticWarning,
} from '../styles/tokens';
import { AgentMarkdown } from './AgentMarkdown';
import { ApprovalCard } from './ApprovalCard';
import { MessageFooter } from './MessageFooter';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ProPlusSpinner = styled.img`
  width: 16px;
  height: 16px;
  animation: ${spin} 1s infinite linear;
`;

const CursorSpan = styled.span<{ isTyping: boolean }>`
  border-right: ${({ isTyping }) => (isTyping ? `2px solid ${colorAgentTextPrimary}` : 'none')};
  padding-right: ${({ isTyping }) => (isTyping ? '2px' : '0')};
`;

const MemoizedMarkdown = memo(function MemoizedMarkdown({ content }: { content: string }) {
  return <AgentMarkdown content={content} />;
});

function StreamingBubble({
  content,
  scrollRef,
  messageId,
  hasPendingFooter,
  onTypingComplete,
}: {
  content: string;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  messageId: string;
  hasPendingFooter: boolean;
  onTypingComplete: (messageId: string) => void;
}) {
  const displayed = useTypewriterMessage(content, 20);
  const throttled = useThrottledValue(displayed, 80);
  const stillTyping = displayed.length < content.length;

  useEffect(() => {
    if (scrollRef?.current) {
      scrollRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
    }
  }, [throttled, scrollRef]);

  useEffect(() => {
    if (!stillTyping && hasPendingFooter) {
      onTypingComplete(messageId);
    }
  }, [stillTyping, hasPendingFooter, messageId, onTypingComplete]);

  return (
    <CursorSpan isTyping={stillTyping}>
      <MemoizedMarkdown content={throttled} />
    </CursorSpan>
  );
}

const Container = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MessageRow = styled(Box)<{ role: 'user' | 'assistant' }>`
  display: flex;
  align-items: flex-start;
  justify-content: ${({ role }) => (role === 'user' ? 'flex-end' : 'flex-start')};
`;

const Bubble = styled(Box)<{ role: 'user' | 'assistant' }>`
  max-width: ${({ role }) => (role === 'user' ? '85%' : '95%')};
  padding: ${({ role }) => (role === 'user' ? '6px 10px' : '2px 4px')};
  border-radius: 2px;
  background-color: ${({ role }) => (role === 'user' ? colorNeutralGray : 'transparent')};
  font-size: 13px;
  line-height: 1.6;
  color: ${colorAgentTextPrimary};
  border: ${({ role }) => (role === 'user' ? `1px solid ${colorAgentBorder}` : 'none')};
`;

const LoadingRow = styled(Box)`
  display: flex;
  align-items: center;
`;

const LoadingBubble = styled(Box)`
  padding: 4px 8px;
  border-radius: 2px;
  background-color: transparent;
  font-size: 12px;
  color: ${colorAgentTextSecondary};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AlertBlock = styled(Box)<{ accentColor: string }>`
  align-self: flex-start;
  max-width: 90%;
  border: 1px solid ${colorAgentBorder};
  border-left: 3px solid ${({ accentColor }) => accentColor};
  border-radius: 2px;
  padding: 6px 8px;
  font-size: 12px;
`;

const AlertActions = styled(Box)`
  display: flex;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
`;

const BosButton = styled.button`
  height: 24px;
  padding: 1px 12px;
  border: 0.8px solid #a3a3a3;
  border-radius: 10px;
  font-size: 12px;
  font-family: 'メイリオ', Meiryo, sans-serif;
  color: #000;
  background-color: #f0f0f0;
  background-image: url(/images/button.png);
  box-shadow: #888 1px 1px 2px 0px;
  cursor: pointer;
  line-height: normal;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    opacity: 0.88;
    background-image: url(/images/button_hover.png);
  }

  &:active {
    box-shadow: #888 0px 0px 1px 0px;
  }

  &:focus-visible {
    outline: 2px solid #002169;
    outline-offset: 1px;
  }
`;

const AlertHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

const AlertText = styled(Typography)`
  font-size: 12px;
`;

const AlertLink = styled.a`
  font-size: 12px;
  color: inherit;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const WarningIcon = styled(WarningAmberIcon)`
  font-size: 16px;
  color: ${colorSemanticWarning};
`;

const SmallIcon = styled.span`
  font-size: 14px;
`;

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  streamingStatus?: StreamingStatus;
  currentToolName?: string | null;
  progressMessage?: string | null;
  skipTypewriter?: boolean;
  error: ChatError | null;
  onRetry?: () => void;
  onTypingComplete: (messageId: string) => void;
  approvalRequest?: ApprovalRequest | null;
  onApprove?: () => void;
  onReject?: () => void;
  onEdit?: (editMessage: string) => void;
}

function ErrorDisplay({ error, onRetry }: { error: ChatError; onRetry?: () => void }) {
  const { statusCode, message } = error;
  const isWarning = statusCode === 404 || statusCode === 401;
  const accentColor = isWarning ? colorSemanticWarning : colorSemanticError;

  if (statusCode === 404) {
    return (
      <AlertBlock accentColor={accentColor}>
        <AlertHeader>
          <WarningIcon />
          <AlertText>{message}</AlertText>
        </AlertHeader>
        <AlertLink href="/user/webhook-token">Webhook トークン設定画面を開く →</AlertLink>
      </AlertBlock>
    );
  }

  if (statusCode === 401) {
    return (
      <AlertBlock accentColor={accentColor}>
        <AlertHeader>
          <WarningIcon />
          <AlertText>セッションが切れました。ページを更新してください。</AlertText>
        </AlertHeader>
        <AlertActions>
          <BosButton
            type="button"
            onClick={() => {
              try {
                sessionStorage.setItem(
                  'agent_last_thread',
                  JSON.stringify({ threadId: window.location.pathname }),
                );
              } catch {
                /* ignore */
              }
              window.location.reload();
            }}
          >
            <SmallIcon as={RefreshIcon} />
            ページを更新
          </BosButton>
        </AlertActions>
      </AlertBlock>
    );
  }

  return (
    <AlertBlock accentColor={accentColor}>
      <AlertText>{message}</AlertText>
      {isRetryableError(error) && onRetry && (
        <AlertActions>
          <BosButton type="button" onClick={onRetry}>
            <SmallIcon as={ReplayIcon} />
            再試行
          </BosButton>
        </AlertActions>
      )}
    </AlertBlock>
  );
}

export function MessageList({
  messages,
  isLoading,
  streamingStatus = 'idle',
  currentToolName,
  progressMessage,
  skipTypewriter = false,
  error,
  onRetry,
  onTypingComplete,
  approvalRequest,
  onApprove,
  onReject,
  onEdit,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef('');

  const lastMessage = messages[messages.length - 1];
  const currentContent = lastMessage?.role === 'assistant' ? lastMessage.content : '';

  useEffect(() => {
    if (currentContent !== lastContentRef.current || streamingStatus !== 'idle') {
      lastContentRef.current = currentContent;
      bottomRef.current?.scrollIntoView({ behavior: streamingStatus === 'streaming' ? 'instant' : 'smooth' });
    }
  }, [currentContent, streamingStatus, messages.length, isLoading, error, approvalRequest]);

  return (
    <Container>
      {messages.map((message, index) => {
        const isLastAssistant = message.role === 'assistant' && index === messages.length - 1;
        const shouldUseTypewriter =
          isLastAssistant && !skipTypewriter && !message.isTypingComplete && message.content.length > 0;
        return (
          <MessageRow key={message.id} role={message.role}>
            <Bubble role={message.role}>
              {message.role === 'assistant' ? (
                shouldUseTypewriter ? (
                  <StreamingBubble
                    content={message.content}
                    scrollRef={bottomRef}
                    messageId={message.id}
                    hasPendingFooter={message.pendingDownloadLinks !== undefined}
                    onTypingComplete={onTypingComplete}
                  />
                ) : (
                  <>
                    <MemoizedMarkdown content={message.content} />
                    <MessageFooter links={message.downloadLinks} content={message.content} />
                  </>
                )
              ) : (
                message.content
              )}
            </Bubble>
          </MessageRow>
        );
      })}
      {streamingStatus === 'thinking' && (
        <LoadingRow>
          <LoadingBubble>
            <ProPlusSpinner src="/images/loading.png" alt="" />
            {progressMessage || '考えています...'}
          </LoadingBubble>
        </LoadingRow>
      )}
      {streamingStatus === 'tool_calling' && (
        <LoadingRow>
          <LoadingBubble>
            <ProPlusSpinner src="/images/loading.png" alt="" />
            {progressMessage || currentToolName || 'ツール実行中'}...
          </LoadingBubble>
        </LoadingRow>
      )}
      {isLoading && streamingStatus === 'idle' && (
        <LoadingRow>
          <LoadingBubble>
            <ProPlusSpinner src="/images/loading.png" alt="" />
            応答を生成中...
          </LoadingBubble>
        </LoadingRow>
      )}
      {error && <ErrorDisplay error={error} onRetry={onRetry} />}
      {approvalRequest && onApprove && onReject && onEdit && (
        <ApprovalCard
          approvalRequest={approvalRequest}
          onApprove={onApprove}
          onReject={onReject}
          onEdit={onEdit}
          disabled={isLoading}
        />
      )}
      <div ref={bottomRef} />
    </Container>
  );
}
